import { useEffect, useCallback } from "react";
import {
  parseDateStr,
  createDate,
  isValidDate,
} from "./date-validation.helpers";

type ValueSyncProps = {
  value?: string;
  setMonth: (value: string) => void;
  setDay: (value: string) => void;
  setYear: (value: string) => void;
  setNativeInputValue: (value: string) => void;
  lastEmittedValue: React.MutableRefObject<string>;
};

/**
 * Syncs external value prop with internal field states
 * Uses a ref to track the last emitted value to prevent feedback loops
 */
export const useValueSync = ({
  value = "",
  setMonth,
  setDay,
  setYear,
  setNativeInputValue,
  lastEmittedValue,
}: ValueSyncProps) => {
  const clearFields = useCallback(() => {
    setMonth("");
    setDay("");
    setYear("");
    setNativeInputValue("");
    lastEmittedValue.current = "";
  }, [setMonth, setDay, setYear, setNativeInputValue, lastEmittedValue]);

  const setFields = useCallback(
    (month: number, day: number, year: number, dateStr: string) => {
      setMonth(String(month).padStart(2, "0"));
      setDay(String(day).padStart(2, "0"));
      setYear(String(year));
      setNativeInputValue(dateStr);
      lastEmittedValue.current = dateStr;
    },
    [setMonth, setDay, setYear, setNativeInputValue, lastEmittedValue],
  );

  useEffect(() => {
    // Only sync if the value changed externally (not from our own emission)
    if (value !== lastEmittedValue.current) {
      if (!value) {
        clearFields();
        return;
      }

      const components = parseDateStr(value);
      if (!components) {
        clearFields();
        return;
      }

      const { year, month, day } = components;
      const date = createDate(year, month, day);

      if (isValidDate(date, year, month, day)) {
        setFields(month, day, year, value);
      } else {
        clearFields();
      }
    }
  }, [
    value,
    setMonth,
    setDay,
    setYear,
    setNativeInputValue,
    clearFields,
    setFields,
    lastEmittedValue,
  ]);
};
